import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Home from "@/pages/Home";
import InfoPage from "@/pages/InfoPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import AurToken from "@/pages/AurToken";
import About from "@/pages/About";
import Admin from "@/pages/Admin";
import Contact from "@/pages/Contact";
import Investors from "@/pages/Investors";
import Whitepaper from "@/pages/Whitepaper";
import { Platform, TokenizedAssets, Partnerships, Compliance, Resources } from "@/pages/ContentPages";
import LeadModal from "@/components/LeadModal";
import "@/App.css";

function useLenis() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/dashboard")) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, [pathname]);
}

function AppRoutes() {
  const [modalOpen, setModalOpen] = useState(false);
  const location = useLocation();
  useLenis();
  const openModal = () => setModalOpen(true);

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <Routes>
        <Route path="/" element={<Home onCta={openModal} />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/signup" element={<AuthPage mode="signup" />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/aur-token" element={<AurToken onCta={openModal} />} />
        <Route path="/about" element={<About onCta={openModal} />} />
        <Route path="/contact" element={<Contact onCta={openModal} />} />
        <Route path="/investors" element={<Investors onCta={openModal} />} />
        <Route path="/whitepaper" element={<Whitepaper onCta={openModal} />} />
        <Route path="/platform" element={<Platform onCta={openModal} />} />
        <Route path="/tokenized-assets" element={<TokenizedAssets onCta={openModal} />} />
        <Route path="/partnerships" element={<Partnerships onCta={openModal} />} />
        <Route path="/compliance" element={<Compliance onCta={openModal} />} />
        <Route path="/resources" element={<Resources onCta={openModal} />} />
        <Route path="/:slug" element={<InfoPage onCta={openModal} />} />
      </Routes>
      <LeadModal open={modalOpen} onOpenChange={setModalOpen} defaultInterest="Investing" />
    </>
  );
}

function App() {
  return (
    <div className="App bg-black min-h-screen text-white">
      <div className="grain" aria-hidden />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
